// Per-plan (B/C/D/S) treemap data for the side panel's distribution-of-power
// block. data/plan-branches/powerAnchors.json is GENERATED by
// `npx tsx scripts/exportSidePanelTabs.ts --site-json` from the hand-set
// D_POWER_ENTITIES / B_POWER_ENTITIES concentration tables — the same
// numbers as the side-data-plan-* sheet tabs' power block. Unlike Plan A's
// anchors builder there is no flagship tail mapping: the named entities keep
// their exact shares, and each region's tail is whatever the constant
// 80/8/12 regional stock leaves over.

import branchPowerJson from "@/data/plan-branches/powerAnchors.json"
import {
  markFrontierByRegionCounts,
  type PowerLabData,
  type PowerLabId,
  type PowerRegionData,
  type PowerRegionId,
} from "../../../../power-bubble-chart/powerBubbleChartData"
import {
  dateIdxForHudCalendarYear,
  tailWeights,
  REGION_TO_BUBBLE,
} from "./companyAnchorsToPowerRegions"

type BranchRegion = "US" | "CH" | "RoW"
type BranchPowerPlan = {
  dates: number[]
  labs: Record<string, { name: string; region: BranchRegion; series: number[] }>
  regionTotals: Record<BranchRegion, number[]>
}

const BRANCH_POWER = branchPowerJson as unknown as Record<
  string,
  BranchPowerPlan | string
>

const M_TO_RAW = 1e6
// Plans C/D concentration-era frontier rule (see withinFactorRule below):
// from 2031 a holder must be within this factor of the largest holder to
// count as frontier.
const WITHIN_FACTOR = 2
const WITHIN_FACTOR_FROM = 2031
// Tail look mirrors Plan A's: one collapsed "Rest of X" cell until the tail
// splits out (2030.67), then a handful of lognormal-weighted companies whose
// count grows slowly with the story date.
const TAIL_SPLIT_FROM = 2030.67
const tailCellCount = (d: number): number =>
  Math.max(4, Math.min(12, 4 + Math.round((d - 2031) * 2)))

// Regions for the treemap on a branch plan at the HUD's calendar year, or
// null when the plan has no branch data (Plan A / no plan) — callers then
// fall through to the Plan A anchors pipeline.
export function getBranchPowerRegions(
  plan: string | null | undefined,
  hudYear: number,
): PowerRegionData[] | null {
  const data = plan ? BRANCH_POWER[plan] : undefined
  if (!data || typeof data === "string") return null
  const idx = dateIdxForHudCalendarYear(data.dates, hudYear)
  const d = data.dates[idx]
  // Plan B's merged US National Project supersedes frontier-by-counts the
  // moment it exists: from the merge on it is the ONLY frontier cell in the
  // WHOLE treemap — always red with its flag — while everything else
  // (the draining legacy labs, the Chinese Megaproject, the UAE project,
  // the anonymous tails) renders border-only.
  const usProjectActive =
    ((data.labs["us-national-project"]?.series[idx] ?? 0) > 0)
  // Plans C/D after 2030: the race concentrates, so frontier status is
  // being-within-2x of the LARGEST holder anywhere (not the sheet's Plan A
  // star counts) — trailing labs fall out of the frontier as the leader
  // compounds its advantage.
  const withinFactorRule =
    (plan === "C" || plan === "D") && d >= WITHIN_FACTOR_FROM
  const frontierComputeFloorM = withinFactorRule
    ? Math.max(
        ...Object.values(data.labs).map((info) => info.series[idx] ?? 0),
      ) / WITHIN_FACTOR
    : 0
  return (Object.keys(REGION_TO_BUBBLE) as BranchRegion[]).map((r) => {
    const def = REGION_TO_BUBBLE[r]
    const named: PowerLabData[] = Object.entries(data.labs)
      .filter(([, info]) => info.region === r)
      .map(([id, info]) => ({
        id: id as PowerLabId,
        name: info.name,
        regionId: def.id,
        compute: (info.series[idx] ?? 0) * M_TO_RAW,
      }))
      .filter((lab) => lab.compute > 0)
    const regionalTotal = (data.regionTotals[r]?.[idx] ?? 0) * M_TO_RAW
    const namedTotal = named.reduce((sum, lab) => sum + lab.compute, 0)
    const tailTotal = Math.max(0, regionalTotal - namedTotal)
    const tailCells = buildTailCells(r, def.id, tailTotal, d)
    // Outside the merged-project era, frontier status comes from the sheet's
    // per-region star counts, with one branch-world override: only NAMED
    // entities take starred slots — the branch tails are synthetic lognormal
    // filler, so Plan A's higher late-year counts must not paint anonymous
    // "X co. N" cells red (the named set on a branch is complete, unlike
    // Plan A's anonymized deal world).
    const labs = usProjectActive
      ? [...named, ...tailCells].map((lab) => ({
          ...lab,
          borderOnly: lab.id !== "us-national-project",
        }))
      : withinFactorRule
        ? [
            ...named.map((lab) => ({
              ...lab,
              borderOnly: lab.compute < frontierComputeFloorM * M_TO_RAW,
            })),
            ...tailCells.map((lab) => ({ ...lab, borderOnly: true })),
          ]
        : [
            ...markFrontierByRegionCounts(named, r, d),
            ...tailCells.map((lab) => ({ ...lab, borderOnly: true })),
          ]
    return {
      id: def.id,
      name: def.name,
      shortName: def.shortName,
      computeStock: regionalTotal,
      labs,
    }
  })
}

function buildTailCells(
  region: BranchRegion,
  regionId: PowerRegionId,
  tailTotal: number,
  d: number,
): PowerLabData[] {
  if (tailTotal <= 0) return []
  const tailLabel =
    region === "US" ? "Rest of US" : region === "CH" ? "Rest of China" : "Rest of World"
  if (d < TAIL_SPLIT_FROM) {
    return [
      {
        id: `${region}-tail` as PowerLabId,
        name: tailLabel,
        regionId,
        compute: tailTotal,
      },
    ]
  }
  // Same seeds as the Plan A builder so tail-company identities stay stable.
  const seed = 31 + (region === "US" ? 0 : region === "CH" ? 100 : 200)
  const weights = tailWeights(tailCellCount(d), "lognormal", 0.3, seed)
  const prefix = region === "US" ? "US" : region === "CH" ? "CN" : "RoW"
  return weights.map((weight, i) => ({
    id: `${region}-tail-${i}` as PowerLabId,
    name: `${prefix} co. ${i + 1}`,
    regionId,
    compute: tailTotal * weight,
  }))
}
